import React, { useEffect, useState } from 'react';
import {
  alternativeStrengths, alternatives, currentStrength, doseStepComplete, fmtUsd,
  prescribedLabel, requestedAtIntake, requestedAtIntakeLabel, rxDisplayName,
} from '@/lib/followupQuote';

// §branded-refill §2.2, generalised by BE #341/#342 — the ONE medication step
// every quoted refill shows, rendered from the quote and never from the plan
// picker. The lists ARE the choice (no "ask for a change" card with nothing to
// price): keep the current Rx, or pick a strength / another form; any pick
// re-quotes with requestedMedId (the parent effect) and needs a reason.
//
// The "At your last visit you asked for X" callout prices X with a read-only
// preview quote and offers "Request this instead". Hidden when the intake
// request was the current med or nothing.
//
// Answer: { choice: 'keep' | 'change', requestedMedId?, requestedLabel?, kind?, reason? }
const RefillDoseStep = ({ question, value, onAnswer, onNext, quote, previewQuote }) => {
  const answer = value || {};
  const cur = currentStrength(quote);
  const strengthOpts = alternativeStrengths(quote);
  const formOpts = alternatives(quote);
  const asked = requestedAtIntake(quote);
  const askedLabel = requestedAtIntakeLabel(asked);
  const canContinue = doseStepComplete(answer);
  const [askedCents, setAskedCents] = useState(null);

  useEffect(() => {
    if (!asked || typeof previewQuote !== 'function') return undefined;
    let alive = true;
    previewQuote(asked.medId).then((q) => {
      if (alive && q && Number.isFinite(Number(q.totalCents))) setAskedCents(Number(q.totalCents));
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asked && asked.medId]);

  const pick = (medId, label, kind) =>
    onAnswer({ ...answer, choice: 'change', requestedMedId: medId, requestedLabel: label || '', kind });
  const keep = () => onAnswer({ choice: 'keep' });

  const altLabel = (a) => a.label || a.name || a.medId;
  const keepTitle = formOpts.length ? 'Keep my current medication' : 'Keep my current dose';
  const rxLabel = rxDisplayName(quote);

  return (
    <div data-testid={`step-${question.id}`}>
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">{question.question}</h2>
      {question.subtitle && <p className="text-center text-gray-500 mb-6">{question.subtitle}</p>}

      {asked && (
        <div className="rounded-xl border border-[#2d8a5f]/30 bg-[#f0faf4] p-4 mb-5" data-testid="refill-asked-at-intake">
          <p className="text-sm text-gray-800">
            At your last visit you asked for <span className="font-semibold">{askedLabel}</span>
            {asked.form ? ` (${asked.form})` : ''}, but your provider prescribed <span className="font-semibold">{prescribedLabel(quote)}</span>.
            {' '}Request {askedLabel} for this refill?
            {askedCents !== null && <span className="text-gray-600"> — {fmtUsd(askedCents)}</span>}
          </p>
          <button
            type="button"
            onClick={() => pick(asked.medId, askedLabel, asked.option === 'alternative' ? 'alternative' : 'strength')}
            className={`mt-3 btn-primary ${answer.requestedMedId === asked.medId ? 'opacity-70' : ''}`}
            data-testid="refill-asked-switch"
          >
            {answer.requestedMedId === asked.medId ? 'Requested — your clinician confirms' : 'Request this instead'}
          </button>
        </div>
      )}

      <div className="space-y-3 mb-6">
        <button
          type="button"
          onClick={keep}
          className={`option-card w-full p-5 rounded-xl text-left transition-all ${answer.choice === 'keep' ? 'selected' : ''}`}
          data-testid="refill-dose-keep"
        >
          <p className="text-base font-medium text-gray-800">{keepTitle}</p>
          <p className="text-sm text-gray-500">
            {rxLabel}{cur && cur.label && !rxLabel.includes(cur.label) ? ` · ${cur.label}` : ''}
          </p>
        </button>
      </div>

      {(strengthOpts.length > 0 || formOpts.length > 0) && (
        <div className="mb-6" data-testid="refill-dose-picker">
          <p className="text-sm font-semibold text-gray-800 mb-3">Or ask for a change</p>
          {strengthOpts.length > 0 && (
            <>
              <p className="text-sm font-medium text-gray-700 mb-2">A different strength</p>
              <div className="space-y-2 mb-4">
                {strengthOpts.map((s, i) => (
                  <button
                    key={s.medId}
                    type="button"
                    onClick={() => pick(s.medId, s.label, 'strength')}
                    className={`option-card w-full p-4 rounded-xl text-left transition-all ${answer.choice === 'change' && answer.requestedMedId === s.medId ? 'selected' : ''}`}
                    data-testid={`refill-dose-option-${i}`}
                  >
                    <p className="text-base text-gray-800">{s.label || s.medId}</p>
                  </button>
                ))}
              </div>
            </>
          )}
          {formOpts.length > 0 && (
            <>
              <p className="text-sm font-medium text-gray-700 mb-2">A different form</p>
              <div className="space-y-2 mb-4">
                {formOpts.map((a, i) => (
                  <button
                    key={a.medId}
                    type="button"
                    onClick={() => pick(a.medId, altLabel(a), 'alternative')}
                    className={`option-card w-full p-4 rounded-xl text-left transition-all ${answer.choice === 'change' && answer.requestedMedId === a.medId ? 'selected' : ''}`}
                    data-testid={`refill-alt-option-${i}`}
                  >
                    <p className="text-base text-gray-800">{altLabel(a)}</p>
                    {a.form && <p className="text-xs text-gray-500 capitalize">{a.form}</p>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {answer.choice === 'change' && answer.requestedMedId && (
        <div className="mb-6" data-testid="refill-dose-reason-block">
          <p className="text-sm text-gray-700 mb-2">
            Requesting <span className="font-semibold">{answer.requestedLabel || answer.requestedMedId}</span> instead of {prescribedLabel(quote)}. Your clinician decides.
          </p>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="refill-dose-reason">
            Why would you like this change?
          </label>
          <textarea
            id="refill-dose-reason"
            value={answer.reason || ''}
            onChange={(e) => onAnswer({ ...answer, choice: 'change', reason: e.target.value })}
            placeholder="e.g., side effects, plateau, cost, my clinician suggested it…"
            rows={3}
            className="form-input w-full"
            data-testid="refill-dose-reason"
          />
        </div>
      )}

      <button onClick={onNext} disabled={!canContinue} className="btn-primary" data-testid="continue-button">
        Continue
      </button>
    </div>
  );
};

export default RefillDoseStep;
